import {
    ApplicationRoleCode,
    CorporationJoinMode,
    KcfHandling,
    LocationJoinMode,
    PagedResultsModel,
    UserResponse,
    UserTag,
} from '@kcftech/prometheus-client';
import {
    DefaultError,
    InfiniteData,
    keepPreviousData,
    useInfiniteQuery,
} from '@tanstack/react-query';
import { usePermissions } from 'contexts/PermissionsContext';
import { sdClient } from 'contexts/SDKClientContext';
import { useDebounce } from 'custom_hooks/useDebounce';
import { IssueUserInfo } from 'pages/Desk/issues/types';
import { useMemo } from 'react';
import { buildOrderBy, constructSearchExpression, Filter } from 'utils/query_v3';

const QueryKey = 'user';
const PageLimit = 10;
const OrderBy = buildOrderBy(['FirstName', 'LastName']);
const DebounceMs = 50;

export type UseIssueUsersOptions = Readonly<{
    searchText: string;
    corporationId?: string;
    locationId?: string;
    isEnabled: boolean;
    isInternal?: boolean;
}>;

export function useIssueUsers({
    searchText,
    corporationId,
    locationId,
    isEnabled,
    isInternal = false,
}: UseIssueUsersOptions) {
    const { canViewKcfUsers } = usePermissions();
    const debouncedSearchText = useDebounce(searchText, DebounceMs);

    const isQueryEnabled = isEnabled && debouncedSearchText.trim().length > 0;

    const { data, isFetching, hasNextPage, fetchNextPage } = useInfiniteQuery<
        PagedResultsModel<UserResponse>,
        DefaultError,
        InfiniteData<PagedResultsModel<UserResponse>>,
        readonly unknown[],
        number
    >({
        queryKey: [QueryKey, debouncedSearchText, corporationId, locationId],
        queryFn: async ({ pageParam = 1 }) => {
            const corporationIds = corporationId ? [corporationId] : undefined;
            const locationIds = locationId ? [locationId] : undefined;
            const whereAdmin = false;

            const searchExpr = constructSearchExpression(debouncedSearchText, [
                'FirstName',
                'LastName',
                'Email',
            ]);

            const filter = Filter.and(
                Filter.eq('IsApproved', true),
                Filter.eq('AcceptedEula', true),
                isInternal
                    ? Filter.neq('ApplicationRoleCode', ApplicationRoleCode.User)
                    : undefined,
                searchExpr,
            );

            const kcfHandling = canViewKcfUsers
                ? KcfHandling.Include
                : KcfHandling.IncludeSentry;

            return sdClient.getUsers({
                corporationIds,
                locationIds,
                corporationJoinMode: CorporationJoinMode.CanViewAt,
                locationJoinMode: LocationJoinMode.CanViewAt,
                whereAdmin,
                kcfHandling,
                includeTags: [UserTag.DeskChat],
                filter,
                page: pageParam,
                pageLimit: PageLimit,
                orderBy: OrderBy,
            });
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage: PagedResultsModel<UserResponse>) =>
            lastPage.CurrentPage < lastPage.TotalPages
                ? lastPage.CurrentPage + 1
                : undefined,
        enabled: isQueryEnabled,
        gcTime: 0,
        placeholderData: keepPreviousData,
    });

    const users = useMemo(
        () =>
            data?.pages.flatMap((page) =>
                page.Result.map<IssueUserInfo>((user) => ({
                    id: user.Id,
                    firstName: user.FirstName,
                    lastName: user.LastName,
                    email: user.Email,
                    role: user.ApplicationRoleCode,
                })),
            ),
        [data],
    );

    return {
        users: isQueryEnabled ? users : undefined,
        isLoading: isFetching,
        hasNextPage,
        fetchNextPage,
    };
}
